import React, { useContext, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useLocation } from "react-router";
import { useHistory } from "react-router-dom";
import { UnprocessableEntity } from "src/lib/go-core/api/exceptions";
import useFlash from "go-alert/AlertMessage";
import { mapCustomFieldsToForm } from "go-app/components/CustomFields";
import CustomFieldsForm from "go-app/components/CustomFields/CustomFieldsForm";
import handleError from "go-app/services/errors";
import handleException from "go-core/api/handleException";
import { ApiError } from "go-core/api/types";
import { FormDirty } from "go-form/components/FormDirty";
import { useCustomErrors } from "go-form/hooks";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { CustomFieldTemplateApi } from "go-segment/services/types";
import { ProductApi } from "../../../../../../../../services/Api/Organization/types";
import { api } from "../../../../../../../../services/Api/api";
import { ProductsMenuContext } from "../../index";
import ProductContext from "../../pages/Index/pages/Edit";
import { ProductStateType } from "../../pages/Index/pages/New";
import { mapProductRecipes } from "../../utils";
import { onParseProductRecipes } from "../Tabs/ProductEdit/ProductEdit";
import ProductAdditionalInfoForm from "./ProductAdditionalInfoForm";
import ProductBarcodesForm from "./ProductBarcodesForm";
import ProductBasicInfoForm from "./ProductBasicInfoForm";
import ProductPackagesForm from "./ProductPackagesForm";
import ProductVolumesTableForm from "./ProductVolumesTableForm";
import ProductRecipeForm from "./Recipe/ProductRecipeForm";

interface Props {
	copiedProduct?: ProductApi;
	customFieldsConfig?: CustomFieldTemplateApi[];
}

const ProductForm = ({ copiedProduct, ...props }: Props) => {
	const { state } = useLocation<ProductStateType>();
	const { product, customFieldsConfig, productType } = useContext(ProductContext);
	const finalCustomFieldsConfig = props.customFieldsConfig || customFieldsConfig;
	const currentProduct = copiedProduct ? copiedProduct : product;
	const { menuState } = useContext(ProductsMenuContext);
	const form = useForm<ProductApi>({
		criteriaMode: "all",
		defaultValues: {
			...currentProduct,
			type: currentProduct?.type || state?.type || "BASIC",
			custom_ids: [...(currentProduct?.custom_ids || []), {}],
			connections: [...(currentProduct?.connections || []), {}],
			custom_fields: mapCustomFieldsToForm(finalCustomFieldsConfig || [], currentProduct?.custom_fields || []),
			recipes: currentProduct ? onParseProductRecipes(currentProduct).recipes : [],
			product_volumes: [...(currentProduct?.product_volumes || []), {}],
		},
	});
	const [loading, setLoading] = useState<boolean>(false);
	const { formState, handleSubmit, watch, setError, reset } = form;
	const { addFlash, addSuccessFlash } = useFlash();
	const { t } = useTranslation();
	const history = useHistory();
	const organization = useSelector(selectOrganization);
	const watchedRecipes = watch("recipes");
	const [currentRecipeIndex, setCurrentRecipeIndex] = useState<number>(
		watchedRecipes?.length ? watchedRecipes.length - 1 : 0
	);
	const firstRender = useRef<boolean>(true);
	const [formErrors, setFormErrors] = useState<ApiError[]>([]);
	const { setErrors, validateCustomErrors } = useCustomErrors(setError);

	useEffect(() => {
		firstRender.current = false;
	}, []);

	useEffect(() => {
		if (!firstRender.current) {
			if (productType === "PACKAGE") handleChangeToBasicProduct();
			if (productType === "BASIC") handleChangeToPackage();
			if (productType === "COMPOSITE") handleChangeToComposite();
		}
	}, [productType]);

	const handleChangeToBasicProduct = () => {
		reset({
			...watch(),
			type: "PACKAGE",
			custom_ids: [...(watch("custom_ids") || [])],
			connections: [...(watch("connections") || [])],
			custom_fields: mapCustomFieldsToForm(finalCustomFieldsConfig || [], watch("custom_fields") || []),
			recipes: mapProductRecipes({ ...watch(), type: "PACKAGE" }),
		});
	};

	const handleChangeToPackage = () => {
		reset({
			...watch(),
			type: "BASIC",
			custom_ids: [...(watch("custom_ids") || [])],
			connections: [...(watch("connections") || [])],
			custom_fields: mapCustomFieldsToForm(finalCustomFieldsConfig || [], watch("custom_fields") || []),
			recipes: mapProductRecipes({ ...watch(), type: "BASIC" }),
		});
	};

	const handleChangeToComposite = () => {
		reset({
			...watch(),
			type: "COMPOSITE",
			custom_ids: [...(watch("custom_ids") || [])],
			connections: [...(watch("connections") || [])],
			custom_fields: mapCustomFieldsToForm(finalCustomFieldsConfig || [], watch("custom_fields") || []),
			recipes: mapProductRecipes({ ...watch(), type: "COMPOSITE" }),
		});
	};

	const onParseDataForm = (data: ProductApi) => {
		data.connections = data?.connections?.filter((connection) => connection?.name);
		data.custom_ids = data.custom_ids.filter((id) => id?.type || id?.value);
		data.product_volumes = data?.product_volumes?.filter((volume) => volume?.label || volume?.volume);
		if (data.recipes) {
			data.recipes.forEach((recipe) => {
				recipe.components = recipe.components.filter(
					(item) => item?.product_id || (!isNaN(item?.recipe_amount) && item.recipe_amount !== null)
				);
			});
			data.recipes = data.recipes.filter((recipe) => recipe.components.length > 0);
		}
		if (data.default_price_net_money?.amount !== undefined && data.default_price_net_money?.amount !== null)
			data.default_price_net_money = {
				...data.default_price_net_money,
				currency: organization?.currency || "PLN",
			};
		else {
			delete data.default_price_net_money;
		}
		return data;
	};

	const onSubmit = handleSubmit(async (data: ProductApi) => {
		if (!validateCustomErrors()) {
			return;
		}

		setLoading(true);
		try {
			const params = {
				include:
					"category,connections,connections_full,custom_fields,custom_ids,tax,volumes,open_linkeds,recipes,recipes.components.product.tax,recipes.components.product",
			};
			const newData = onParseDataForm(data);
			let res: ProductApi;
			if (currentProduct?.id) {
				res = await api.organization().updateProduct(newData, params);
				if (currentProduct.type === "PACKAGE") {
					const newIndex = (res?.recipes?.length || 1) - 1;
					setCurrentRecipeIndex(newIndex);
				}
				reset({
					...res,
					custom_fields: mapCustomFieldsToForm(finalCustomFieldsConfig || [], res.custom_fields || []),
					recipes: onParseProductRecipes(res).recipes,
					custom_ids: [...(res?.custom_ids || []), {}],
					connections: [...(res?.connections || []), {}],
					product_volumes: [...(res?.product_volumes || []), {}],
				});
			} else {
				res = await api.organization().createProduct(newData);
				history.push(`/${organization.id}/products/${res.id}/edit`);
			}
			addSuccessFlash(t("lib:common.flash.saved"));
		} catch (err) {
			let recursiveProductComponentError: ApiError | undefined = undefined;

			if (err instanceof UnprocessableEntity) {
				recursiveProductComponentError = err.errors.find(
					(error) => error.message === "recursive_product_component"
				);
			}

			if (recursiveProductComponentError && recursiveProductComponentError.arguments?.resource_name) {
				addFlash({
					type: "danger",
					msg: t("constraints.recursive_product_component", {
						element_name: recursiveProductComponentError.arguments.resource_name,
					}),
					messageAlreadyCreated: true,
				});
			} else {
				handleError.form(err, setError, addFlash, ["connections", "custom_ids"]);
				const exceptionErrors = handleException(err);
				setFormErrors([...exceptionErrors]);
			}
		}
		setLoading(false);
	});

	return (
		<FormDirty
			key="product-form"
			noValidate
			formState={formState}
			loading={loading}
			onSubmit={(data) => {
				onSubmit(data);
			}}
		>
			<h5>{t("common.word.basic_info")}</h5>
			<ProductBasicInfoForm form={form} product={currentProduct} setErrors={setErrors} />
			{finalCustomFieldsConfig && finalCustomFieldsConfig.length > 0 && (
				<fieldset className="form-group">
					<h5>{t("common.word.custom_fields")}</h5>
					<CustomFieldsForm
						customFieldsConfig={finalCustomFieldsConfig}
						customFields={currentProduct?.custom_fields || []}
						form={form}
						setErrors={setErrors}
					/>
				</fieldset>
			)}

			{(watch("type") === "PACKAGE" ||
				watch("type") === "COMPOSITE" ||
				(!watch("type") && (menuState === "packages" || menuState === "composites"))) && (
				<ProductRecipeForm
					form={form}
					setCurrentRecipeIndex={setCurrentRecipeIndex}
					currentRecipeIndex={currentRecipeIndex}
				/>
			)}

			<h5 className="mb-1">{t("modules.product.field.additional_info.title")}</h5>
			<span className="text-muted mb-3 d-block">{t("modules.product.field.additional_info.help_text")}</span>
			<ProductAdditionalInfoForm form={form} />

			<h5 className="mb-1">{t("modules.product.field.product_id.title")}</h5>
			<span className="text-muted mb-3 d-block">{t("modules.product.field.product_id.help_text")}</span>
			<ProductBarcodesForm form={form} formErrors={formErrors} />

			{(watch("type") === "BASIC" || (!watch("type") && menuState === "products")) && (
				<>
					<h5 className="mb-1">{t("modules.product.field.packages.title")}</h5>
					<span className="text-muted mb-3 d-block">{t("modules.product.field.packages.help_text")}</span>
					<ProductPackagesForm form={form} formErrors={formErrors} />
				</>
			)}
			<ProductVolumesTableForm form={form} />
		</FormDirty>
	);
};

export default ProductForm;
